import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIncidentStore } from '@/store/incident.store';
import { useTenantStore } from '@/store/tenant.store';
import { api } from '@/services/api';
import { toast } from '@/utils/toast';
import type { DamageAssessment, DamageSeverity } from '@/types/damage-assessment';
import { SEVERITY_CONFIG } from '@/types/damage-assessment';
import FaIcon from '@/components/common/FaIcon';
import EmptyState from '@/components/common/EmptyState';
import Alert from '@/components/common/Alert';
import Badge from '@/components/common/Badge';
import MetricValue from '@/components/common/MetricValue';
import { SEVERITY_SPINE, type Severity } from '@/components/common/severity';

// FEMA damage severity → operational visual-weight scale
const DA_SEVERITY_RANK: Record<DamageSeverity, Severity> = {
  destroyed: 'critical',
  major: 'warning',
  minor: 'elevated',
  affected: 'calm',
};
import { PlusIcon } from '@/components/icons';
import LoadingState from '@/components/common/LoadingState';
import StatusDot from '@/components/common/StatusDot';
import Button, { IconButton } from '@/components/common/Button';
import { damageAssessmentOfflineService, cachedToDamageAssessment } from '@/offline/damage-assessment.service';
import { db } from '@/offline/db';
import { mustDeferSend } from '@/offline/client-id';
import { ownConflictOrNull, resolvableConflicts } from '@/offline/conflict-guard';
import type { CachedDamageAssessment } from '@/offline/db';

// One request never sees the whole incident. The list route pages, and its Zod
// schema caps `limit` at 100, so the page count below is a real cursor — not a
// cosmetic control.
const PAGE_SIZE = 50;

export default function FieldDamageAssessments() {
  const navigate = useNavigate();
  const { activeIncident } = useIncidentStore();
  const { activeTenant } = useTenantStore();
  const [assessments, setAssessments] = useState<DamageAssessment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFromCache, setIsFromCache] = useState(false);
  // Server-reported count for this incident + how many more pages exist.
  //
  // WHY these exist: the page used to request one 50-row page, throw away the
  // server's `total`/`totalPages`, and label the rows it happened to hold as
  // "N total" with no way to reach the rest. On a 180-assessment tornado the
  // assessor was told 50 assessments existed, could not scroll to the other
  // 130, and re-surveyed addresses already documented — inflating the PDA
  // counts that drive the FEMA declaration threshold. The label was wrong in
  // both directions too: locally-pending rows are merged into the list below,
  // so the number shown could also EXCEED the page size while still hiding
  // hundreds of server rows.
  const [serverTotal, setServerTotal] = useState(0);
  const [localOnlyCount, setLocalOnlyCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Server rows accumulated across pages; the pending-row merge below must
  // dedupe against ALL of them, not just the newest page.
  const serverRowsRef = useRef<DamageAssessment[]>([]);
  const [conflicts, setConflicts] = useState<CachedDamageAssessment[]>([]);
  const [resolvingConflict, setResolvingConflict] = useState<CachedDamageAssessment | null>(null);
  const [erroredAssessments, setErroredAssessments] = useState<CachedDamageAssessment[]>([]);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const loadAssessments = useCallback(async (pageNo = 1) => {
    try {
      if (pageNo > 1) setIsLoadingMore(true); else setIsLoading(true);
      setIsFromCache(false);
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), page: String(pageNo) });
      if (activeIncident?.id) params.append('incident_id', activeIncident.id);
      // The list controller responds with the BARE paginated object
      // ({ data, total, page, limit, totalPages }) and api.get returns the raw
      // body — so `total`/`totalPages` are available here and must not be
      // discarded, or the header lies and pages 2+ become unreachable.
      const data = await api.get<{
        data: DamageAssessment[];
        total?: number;
        totalPages?: number;
      }>(`/damage-assessments?${params}`);
      const pageRows = data.data || [];
      const serverList = pageNo > 1 ? [...serverRowsRef.current, ...pageRows] : pageRows;
      serverRowsRef.current = serverList;

      const total = Number(data.total);
      const totalPages = Number(data.totalPages);
      setServerTotal(Number.isFinite(total) ? total : serverList.length);
      setPage(pageNo);
      setHasMore(
        Number.isFinite(totalPages) && totalPages > 0
          ? pageNo < totalPages
          : pageRows.length === PAGE_SIZE
      );
      // Merge locally-pending rows into the ONLINE list too.
      //
      // WHY: an assessment created offline whose sync POST failed still carries
      // id === offline_id === "offline_<uuid>" (the id is only rewritten on a
      // successful POST). It is not in the server response, so if we render the
      // server list alone that record exists nowhere on this page — while the
      // amber banner below, driven by the SAME cached rows, tells the assessor
      // "N assessment(s) failed to sync — tap Retry on each record below". There
      // is no such row and no Retry button to tap; when the server list is empty
      // the page even shows "No assessments yet" directly under the failure
      // banner. The assessor cannot see what they captured for that structure,
      // cannot inspect it, and has no manual recovery path — their fieldwork is
      // stranded in IndexedDB. Server row always wins on a collision.
      let merged = serverList;
      let localOnlyRows = 0;
      if (activeTenant?.id) {
        try {
          const pending = (await damageAssessmentOfflineService.getAll(activeTenant.id, activeIncident?.id))
            .filter(c => !c.is_synced);
          const serverIds = new Set<string>();
          for (const s of serverList) {
            serverIds.add(s.id);
            if (s.offline_id) serverIds.add(s.offline_id);
          }
          const localOnly = pending.filter(
            c => !serverIds.has(c.id) && !(c.offline_id && serverIds.has(c.offline_id))
          );
          // Translate, never cast — see cachedToDamageAssessment: the cache keys
          // severity as `overall_severity`, so a cast would badge/sort every
          // merged row as the mildest category and print "#undefined".
          localOnlyRows = localOnly.length;
          if (localOnly.length > 0) merged = [...serverList, ...localOnly.map(cachedToDamageAssessment)];
        } catch { /* cache unreadable — the server list still renders */ }
      }
      setAssessments(merged);
      // Counted into the header total so the denominator stays consistent with
      // the rows actually rendered (server rows + local-only rows).
      setLocalOnlyCount(localOnlyRows);
      // Cache results for offline use. Page 1 only: cacheFromServer pages
      // through the whole incident itself, so re-running it per "Load more"
      // would just replay the same full sync.
      if (pageNo === 1 && activeTenant?.id && pageRows.length) {
        damageAssessmentOfflineService.cacheFromServer(activeTenant.id, activeIncident?.id).catch(() => {});
      }
    } catch {
      // Fall back to offline cache
      try {
        const cached = await damageAssessmentOfflineService.getAll(activeTenant?.id || '', activeIncident?.id);
        if (cached.length > 0) {
          // Translate — do NOT cast. The cache stores severity under
          // `overall_severity` and structure under `property_type`, so casting
          // leaves `a.severity` undefined on every row: the badge below falls
          // back to "Affected" for DESTROYED structures and the worst-first
          // sort comparator collapses to a no-op, burying total losses among
          // minor ones. Offline is exactly when an operator prioritises
          // re-entry and FEMA escalation off this list.
          setAssessments(cached.map(cachedToDamageAssessment));
          setIsFromCache(true);
          // Offline: the cache IS the whole set we can show, so the header must
          // count it — not a stale server total — and there is nothing to page.
          serverRowsRef.current = [];
          setServerTotal(cached.length);
          setLocalOnlyCount(0);
          setPage(1);
          setHasMore(false);
        } else {
          toast.error('Failed to load assessments');
        }
      } catch {
        toast.error('Failed to load assessments');
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [activeIncident?.id, activeTenant?.id]);

  const loadConflicts = useCallback(async () => {
    try {
      const conflicted = await db.cachedDamageAssessments
        .where('conflict_state')
        .equals('detected')
        .toArray();
      // Only rows this session may send (the same gate every sync path uses): another
      // originator's conflict is theirs to resolve, never resolved and sent as this user.
      setConflicts(resolvableConflicts(conflicted));
    } catch { /* ignore */ }
  }, []);

  const loadErrored = useCallback(async () => {
    try {
      // Scope to the same tenant + incident the list itself was loaded for.
      // Unscoped, stale errored rows from another tenant or another incident
      // inflate the banner count above the number of rows that can actually
      // render a Retry button — the banner would again point at records the
      // assessor cannot see or act on.
      const tenantId = activeTenant?.id;
      const incidentId = activeIncident?.id;
      // Only rows this session may send get a Retry button: the same gate every sync path
      // uses (another originator's, an unknown originator's or another tenant's row is not).
      const errored = await db.cachedDamageAssessments
        .filter(a =>
          (!!a.last_error || a.photos_synced === false) &&
          (!tenantId || a.tenant_id === tenantId) &&
          (!incidentId || a.incident_id === incidentId) &&
          !mustDeferSend(a))
        .toArray();
      setErroredAssessments(errored);
    } catch { /* ignore */ }
  }, [activeTenant?.id, activeIncident?.id]);

  const retrySync = useCallback(async (assessmentId: string) => {
    setRetryingId(assessmentId);
    try {
      const success = await damageAssessmentOfflineService.syncAssessment(assessmentId);
      if (success) {
        toast.success('Sync successful');
      } else {
        toast.error('Sync still failing — will retry automatically when conditions improve');
      }
      await loadErrored();
    } catch {
      toast.error('Retry failed');
    } finally {
      setRetryingId(null);
    }
  }, [loadErrored]);

  const resolveConflict = useCallback(async (assessment: CachedDamageAssessment, keepLocal: boolean) => {
    try {
    // Only this session's own change may be resolved here (offline/conflict-guard.ts): "keep
    // server" would overwrite another originator's pending change, "keep local" would send it
    // as this user.
    if (!(await ownConflictOrNull(db.cachedDamageAssessments, assessment))) {
      toast.error('This change belongs to another user or tenant; it stays for them.');
      setResolvingConflict(null);
      await loadConflicts();
      return;
    }
      if (keepLocal) {
        // Keep local version: clear conflict state and mark as pending sync
        await db.cachedDamageAssessments.update(assessment.id, {
          conflict_state: 'resolved',
          pending_changes: true,
          is_synced: false,
        });
      } else {
        // Use server version: replace local with the server snapshot
        if (assessment.server_snapshot) {
          await db.cachedDamageAssessments.put({
            ...assessment,
            ...assessment.server_snapshot,
            id: assessment.id,
            tenant_id: assessment.tenant_id,
            conflict_state: 'resolved',
            is_synced: true,
            pending_changes: false,
            server_snapshot: undefined,
          } as CachedDamageAssessment);
        } else {
          await db.cachedDamageAssessments.update(assessment.id, {
            conflict_state: 'resolved',
          });
        }
      }
      setResolvingConflict(null);
      await loadConflicts();
      toast.success('Conflict resolved');
    } catch {
      toast.error('Failed to resolve conflict');
    }
  }, [loadConflicts]);

  useEffect(() => {
    loadAssessments();
    loadConflicts();
    loadErrored();
  }, [loadAssessments, loadConflicts, loadErrored]);

  if (isLoading) {
    return <LoadingState message="Loading assessments..." />;
  }

  // Rendered rows vs. everything that exists for this incident. Local-only rows
  // are part of the rendered list, so they belong in the denominator too.
  const totalKnown = serverTotal + localOnlyCount;

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="heading-3">Damage Assessments</h2>
        <div className="flex items-center gap-2">
          <span className="text-sm text-theme-tertiary">
            {assessments.length < totalKnown ? (
              <>
                <MetricValue value={assessments.length} className="font-semibold" /> of{' '}
                <MetricValue value={totalKnown} />
              </>
            ) : (
              <>
                <MetricValue value={assessments.length} className="font-semibold" /> total
              </>
            )}
          </span>
          <IconButton
            icon={<FaIcon icon="arrows-rotate" className="h-5 w-5" />}
            label="Refresh assessments"
            onClick={() => loadAssessments()}
          />
        </div>
      </div>

      {isFromCache && (
        <div className="flex items-center gap-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <StatusDot color="yellow" pulse />
          <span className="text-xs text-yellow-700 dark:text-yellow-300">Showing cached data (offline)</span>
        </div>
      )}

      {erroredAssessments.length > 0 && (
        <Alert
          variant="warning"
          compact
          title={`${erroredAssessments.length} assessment${erroredAssessments.length > 1 ? 's' : ''} failed to sync — tap "Retry" on each record below`}
        >
          {erroredAssessments.filter(a => a.photos_synced === false && a.is_synced).length > 0 && (
            <span className="block text-xs mt-1">
              Some assessments synced but photos could not be uploaded.
            </span>
          )}
        </Alert>
      )}

      {conflicts.length > 0 && !resolvingConflict && (
        <Alert
          variant="warning"
          title={`${conflicts.length} assessment${conflicts.length > 1 ? 's have' : ' has'} conflicts with server data`}
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={() => setResolvingConflict(conflicts[0])}
            >
              Review
            </Button>
          }
        >
          Local changes conflict with newer server data. Review to resolve.
        </Alert>
      )}

      {resolvingConflict && (
        <div className="bg-surface-primary rounded-lg border border-amber-300 dark:border-amber-700 p-4 space-y-3">
          <p className="text-sm font-medium text-theme-primary">
            Conflict {conflicts.indexOf(resolvingConflict) + 1} of {conflicts.length}: {resolvingConflict.address || 'No address'}
          </p>
          <p className="text-xs text-theme-tertiary">
            Your local version conflicts with the server version. Choose which to keep:
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => resolveConflict(resolvingConflict, true)}
            >
              Keep My Version
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => resolveConflict(resolvingConflict, false)}
            >
              Use Server Version
            </Button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const currentIdx = conflicts.indexOf(resolvingConflict);
              const nextIdx = currentIdx + 1;
              if (nextIdx < conflicts.length) {
                setResolvingConflict(conflicts[nextIdx]);
              } else {
                setResolvingConflict(null);
              }
            }}
            className="w-full text-xs"
          >
            {conflicts.indexOf(resolvingConflict) + 1 < conflicts.length ? 'Skip to Next' : 'Decide Later'}
          </Button>
        </div>
      )}

      {assessments.length === 0 ? (
        <EmptyState
          variant="operational"
          icon="clipboard-check"
          title="No assessments yet"
          description="Create a damage assessment to get started."
          actionLabel="New Assessment"
          onAction={() => navigate('/field/damage/new')}
        />
      ) : (
        <div className="space-y-2">
          {[...assessments].sort((a, b) => {
            const order: Record<DamageSeverity, number> = { destroyed: 0, major: 1, minor: 2, affected: 3 };
            return (order[a.severity] ?? 4) - (order[b.severity] ?? 4);
          }).map((a) => {
            const sev = SEVERITY_CONFIG[a.severity] || SEVERITY_CONFIG.affected;
            const rank = DA_SEVERITY_RANK[a.severity] ?? 'calm';
            const cached = erroredAssessments.find(e => e.id === a.id || e.offline_id === a.id);
            const hasDataError = cached?.last_error && !cached?.photos_synced && !cached?.is_synced;
            const hasPhotoError = cached?.photos_synced === false && cached?.is_synced;
            const isRetrying = retryingId === a.id || retryingId === (cached?.id);
            // Merged cache-only row: never reached the server, so it must not
            // read as an ordinary filed assessment.
            const isLocalOnly = a.id.startsWith('offline_');
            return (
              <div
                key={a.id}
                className={`bg-surface-primary rounded-lg border p-3 ${SEVERITY_SPINE[rank]} ${
                  hasDataError || hasPhotoError
                    ? 'border-amber-300 dark:border-amber-700'
                    : 'border-theme-primary'
                }`}
              >
                <button
                  onClick={() => navigate(`/field/damage/${a.id}`)}
                  className="w-full text-left touch-manipulation min-h-[44px]"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-theme-primary truncate">
                        {a.address || 'No address'}
                      </p>
                      <p className="text-xs text-theme-tertiary mt-0.5">
                        <MetricValue value={`#${a.assessment_number}`} /> &middot; {a.structure_type?.replace(/_/g, ' ')}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {hasPhotoError && (
                        <Badge variant="warning" icon={<FaIcon icon="image" className="h-3 w-3" />}>
                          Photos pending
                        </Badge>
                      )}
                      {hasDataError && (
                        <Badge variant="danger" pulse icon={<FaIcon icon="triangle-exclamation" className="h-3 w-3" />}>
                          Sync failed
                        </Badge>
                      )}
                      {isLocalOnly && !hasDataError && (
                        <Badge variant="neutral" icon={<FaIcon icon="cloud-arrow-up" className="h-3 w-3" />}>
                          Not synced
                        </Badge>
                      )}
                      <Badge variant={sev.variant} size="sm" pulse={rank === 'critical'}>
                        {sev.label}
                      </Badge>
                    </div>
                  </div>
                </button>
                {(hasDataError || hasPhotoError) && cached && (
                  <div className="mt-2 pt-2 border-t border-amber-100 dark:border-amber-900/40 flex items-start justify-between gap-2">
                    <p className="text-xs text-amber-700 dark:text-amber-300 truncate">
                      {hasDataError ? cached.last_error : `${cached.unsynced_photo_count ?? 'Some'} photo(s) failed to upload`}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); retrySync(cached.id); }}
                      disabled={isRetrying}
                      loading={isRetrying}
                    >
                      Retry
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Cursor to the rest of the incident. Without it every assessment past
          the first page is unreachable from the field view and the assessor
          re-surveys structures that are already documented. */}
      {hasMore && (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => loadAssessments(page + 1)}
          disabled={isLoadingMore}
          loading={isLoadingMore}
        >
          Load more
        </Button>
      )}

      {/* FAB */}
      <button
        onClick={() => navigate('/field/damage/new')}
        aria-label="Create new assessment"
        className="fixed right-4 z-30 w-14 h-14 bg-primary-600 text-white rounded-full shadow-lg flex items-center justify-center touch-manipulation active:bg-primary-700"
        style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom) + 1rem)' }}
      >
        <PlusIcon className="h-7 w-7" />
      </button>
    </div>
  );
}
